import React from 'react';
import { useParams, Link } from 'react-router-dom';

interface HelpArticle {
  title: string;
  category: string;
  lastUpdated: string;
  content: string;
  relatedArticles: {
    title: string;
    slug: string;
  }[];
}

interface HelpArticles {
  [key: string]: HelpArticle;
}

const helpArticles: HelpArticles = {
  'creating-account': {
    title: 'Creating Your Account',
    category: 'Getting Started',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>How to Create Your Account</h2>
      <p>To access all features of 11th Admissions, you need to create a free account. Here's how:</p>
      <ol>
        <li>Go to the <strong>Sign Up</strong> page from the top right corner.</li>
        <li>Enter your email address and create a secure password.</li>
        <li>Verify your email address by clicking the link sent to your inbox.</li>
        <li>Fill in your basic details (name, city, school, etc.).</li>
        <li>Click <strong>Create Account</strong> to finish.</li>
      </ol>
      <h3>Troubleshooting</h3>
      <ul>
        <li>If you don't receive the verification email, check your spam folder.</li>
        <li>Make sure your password is at least 8 characters long and includes a number.</li>
      </ul>
      <h3>Why Register?</h3>
      <ul>
        <li>Save your progress and preferences</li>
        <li>Get personalized college recommendations</li>
        <li>Access premium features and support</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'Uploading your marksheet', slug: 'uploading-marksheet' },
      { title: 'Account security', slug: 'account-security' }
    ]
  },
  'uploading-marksheet': {
    title: 'Uploading Your Marksheet',
    category: 'Getting Started',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>How to Upload Your Marksheet</h2>
      <p>Uploading your marksheet allows us to provide accurate college predictions and cutoffs.</p>
      <ol>
        <li>Log in to your account.</li>
        <li>Go to your <strong>Profile</strong> or <strong>Dashboard</strong>.</li>
        <li>Click <strong>Upload Marksheet</strong>.</li>
        <li>Select your marksheet file (PDF, JPG, or PNG).</li>
        <li>Click <strong>Submit</strong>.</li>
      </ol>
      <h3>Tips</h3>
      <ul>
        <li>Ensure your marksheet is clear and all details are visible.</li>
        <li>Only upload your official 10th standard marksheet.</li>
      </ul>
      <h3>Privacy</h3>
      <p>Your marksheet is securely stored and only used for admission prediction purposes.</p>
    `,
    relatedArticles: [
      { title: 'Creating your account', slug: 'creating-account' },
      { title: 'Understanding your dashboard', slug: 'understanding-dashboard' }
    ]
  },
  'understanding-dashboard': {
    title: 'Understanding Your Dashboard',
    category: 'Getting Started',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Your Dashboard Overview</h2>
      <p>The dashboard is your personal hub for all admission activities. Here's what you'll find:</p>
      <ul>
        <li><strong>Profile Summary:</strong> Your details and academic info</li>
        <li><strong>College Predictor:</strong> Get personalized college recommendations</li>
        <li><strong>Saved Colleges:</strong> Bookmark colleges you're interested in</li>
        <li><strong>Application Status:</strong> Track your application progress</li>
        <li><strong>Notifications:</strong> Important updates and deadlines</li>
      </ul>
      <h3>Tips</h3>
      <ul>
        <li>Update your profile for the most accurate predictions.</li>
        <li>Check notifications regularly for important updates.</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'Uploading your marksheet', slug: 'uploading-marksheet' },
      { title: 'Finding colleges', slug: 'finding-colleges' }
    ]
  },
  'finding-colleges': {
    title: 'Finding Colleges',
    category: 'Getting Started',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>How to Find Colleges</h2>
      <p>Use our search and filter tools to discover colleges that match your preferences:</p>
      <ol>
        <li>Go to the <strong>College Predictor</strong> or <strong>College Database</strong> page.</li>
        <li>Enter your marks, stream, and category.</li>
        <li>Apply filters for city, college type, and medium.</li>
        <li>Browse the list of recommended colleges.</li>
        <li>Click on a college to view details, cutoffs, and reviews.</li>
      </ol>
      <h3>Tips</h3>
      <ul>
        <li>Use the <strong>Compare</strong> feature to shortlist and compare colleges.</li>
        <li>Bookmark your favorite colleges for quick access.</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'Understanding your dashboard', slug: 'understanding-dashboard' },
      { title: 'How to compare colleges', slug: 'comparing-colleges' }
    ]
  },
  'comparing-colleges': {
    title: 'How to Compare Colleges',
    category: 'College Selection',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Comparing Colleges</h2>
      <p>Comparing colleges helps you make an informed decision. Here's how to use our comparison tool:</p>
      <ol>
        <li>Go to the <strong>College Database</strong> or <strong>College Predictor</strong> page.</li>
        <li>Select up to 5 colleges by clicking the <strong>Compare</strong> button.</li>
        <li>Click <strong>Compare Now</strong> to view a side-by-side comparison.</li>
      </ol>
      <h3>What to Compare</h3>
      <ul>
        <li>Cutoff trends and predictions</li>
        <li>College rankings and reviews</li>
        <li>Fees and facilities</li>
        <li>Location and accessibility</li>
      </ul>
      <h3>Tips</h3>
      <ul>
        <li>Look beyond cutoffs—consider campus life, placements, and faculty.</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'Finding colleges', slug: 'finding-colleges' },
      { title: 'College rankings explained', slug: 'college-rankings' }
    ]
  },
  'understanding-cutoffs': {
    title: 'Understanding Cutoffs',
    category: 'College Selection',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>What Are College Cutoffs?</h2>
      <p>Cutoffs are the minimum marks required for admission to a particular college and stream. They vary each year based on:</p>
      <ul>
        <li>Number of applicants</li>
        <li>Available seats</li>
        <li>Reservation categories</li>
        <li>Previous year trends</li>
      </ul>
      <h3>How to Use Cutoff Data</h3>
      <ul>
        <li>Check last year's cutoffs for your category and stream.</li>
        <li>Use predicted cutoffs to estimate your chances.</li>
        <li>Apply to colleges where your marks are above the cutoff for higher chances.</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'How to compare colleges', slug: 'comparing-colleges' },
      { title: 'Understanding cutoff predictions', slug: 'cutoff-predictions' }
    ]
  },
  'college-rankings': {
    title: 'College Rankings Explained',
    category: 'College Selection',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>How Are Colleges Ranked?</h2>
      <p>Our rankings are based on a combination of:</p>
      <ul>
        <li>Academic performance</li>
        <li>Infrastructure and facilities</li>
        <li>Placement records</li>
        <li>Student reviews</li>
        <li>Faculty quality</li>
      </ul>
      <h3>How to Use Rankings</h3>
      <ul>
        <li>Use rankings as a guide, not the only factor.</li>
        <li>Consider your own preferences and priorities.</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'How to compare colleges', slug: 'comparing-colleges' },
      { title: 'Tips for selecting the right stream', slug: 'stream-selection-tips' }
    ]
  },
  'selecting-stream': {
    title: 'Selecting Your Stream',
    category: 'College Selection',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Choosing the Right Stream</h2>
      <p>Choosing between Science, Commerce, and Arts is a big decision. Consider:</p>
      <ul>
        <li>Your interests and strengths</li>
        <li>Career goals</li>
        <li>Advice from teachers and counselors</li>
        <li>Future study and job opportunities</li>
      </ul>
      <h3>Tips</h3>
      <ul>
        <li>Don't choose a stream just because your friends are.</li>
        <li>Research career paths for each stream.</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'Tips for selecting the right stream', slug: 'stream-selection-tips' },
      { title: 'How to compare colleges', slug: 'comparing-colleges' }
    ]
  },
  'government-directives-2025': {
    title: 'Government Directives for 11th Admissions (2025-26)',
    category: 'Admission Process',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>11th Grade Online Admission – Academic Year 2025–26</h2>
      <p>The Maharashtra State Education Department has released new guidelines for the 11th standard (FYJC) admission process. Here's a comprehensive summary of the key directives.</p>

      <h3>Statewide Online Admission System</h3>
      <p>Starting from the academic year 2025-26, all admissions to recognized higher secondary schools (11th standard) across Maharashtra will be conducted through a centralized online system. This applies to all streams:</p>
      <ul>
        <li>Science</li>
        <li>Commerce</li>
        <li>Arts</li>
      </ul>

      <h3>Centralized Admission System (CAS)</h3>
      <p>The new system implements the following key features:</p>
      <ul>
        <li>One centralized online form for all junior colleges</li>
        <li>Strictly merit-based admissions using 10th-grade marks (best of 5 subjects)</li>
        <li>Standardized process across all institutions</li>
      </ul>

      <h3>Quota Reservations</h3>
      <p>The following quota system will be implemented:</p>
      <ul>
        <li><strong>Minority Quota:</strong> 50% (non-transferable during initial 3 rounds)</li>
        <li><strong>Management Quota:</strong> 5% (filled until 2nd round, else transferred)</li>
        <li><strong>In-house Quota:</strong> 10% (only for students from the same school's SSC division)</li>
      </ul>

      <p>Additional parallel reservations apply for:</p>
      <ul>
        <li>Women</li>
        <li>Persons with disabilities</li>
        <li>Project-affected individuals</li>
        <li>Children of active/retired soldiers</li>
        <li>Sports achievers</li>
        <li>Orphans</li>
      </ul>

      <h3>Admission Rounds</h3>
      <p>The admission process will consist of:</p>
      <ul>
        <li>4 regular merit-based rounds</li>
        <li>1 "Open for All" round</li>
      </ul>

      <p><strong>Important Note:</strong> Students who don't accept admission in their first preference college will be disqualified from further regular rounds.</p>

      <h3>Key Dates for 2025-26</h3>
      <div class="bg-[#F0E9FF] p-4 rounded-lg mb-6">
        <ul>
          <li><strong>College Registration/Update:</strong> May 8–15, 2025</li>
          <li><strong>Student Application & Preference Filing:</strong> May 19–28, 2025</li>
          <li><strong>Start of 11th Classes:</strong> August 11, 2025</li>
        </ul>
      </div>

      <h3>Document Verification</h3>
      <p>The following rules apply for document verification:</p>
      <ul>
        <li>Strict verification of all submitted documents</li>
        <li>Immediate disqualification for falsification of documents or SSC marks</li>
        <li>Mandatory caste certificate at the time of admission for reserved category applicants</li>
      </ul>

      <h3>Fees and Refunds</h3>
      <p>The fee structure and refund policy includes:</p>
      <ul>
        <li>₹100 online application fee per student</li>
        <li>Refund policy for admission cancellation:
          <ul>
            <li>5% deduction within first 20 days</li>
            <li>10% deduction within next 40 days</li>
            <li>No refund beyond this period</li>
          </ul>
        </li>
      </ul>

      <h3>Penalties for Non-Compliance</h3>
      <p>Private institutions may face the following penalties:</p>
      <ul>
        <li>₹50,000 to ₹1,00,000 fine for:
          <ul>
            <li>Delaying updates</li>
            <li>Providing misleading information</li>
            <li>Non-compliance with directives</li>
          </ul>
        </li>
      </ul>

      <h3>Technical Platform</h3>
      <p>Important technical requirements:</p>
      <ul>
        <li>All admissions must be processed through the official online portal</li>
        <li>No offline admissions are permitted outside the system</li>
        <li>Institutions must maintain digital records of all admissions</li>
      </ul>

      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
        <p class="text-yellow-800"><strong>Important Note:</strong> These directives are subject to updates from the Maharashtra State Education Department. Students and institutions should regularly check the official website for any amendments or new notifications.</p>
      </div>
    `,
    relatedArticles: [
      { title: 'Understanding College Cutoffs', slug: 'understanding-cutoffs' },
      { title: 'Required Documents for Admission', slug: 'required-documents' },
      { title: 'Merit List Process', slug: 'merit-list-process' }
    ]
  },
  'application-timeline': {
    title: 'Application Timeline',
    category: 'Admission Process',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>11th Admission Timeline 2025-26</h2>
      <p>Here's a detailed timeline for the 11th standard admission process:</p>

      <div class="bg-[#F0E9FF] p-4 rounded-lg mb-6">
        <h3 class="font-semibold mb-2">Key Dates</h3>
        <ul class="space-y-2">
          <li><strong>May 8-15, 2025:</strong> College Registration/Update</li>
          <li><strong>May 19-28, 2025:</strong> Student Application & Preference Filing</li>
          <li><strong>June 1-5, 2025:</strong> Document Verification</li>
          <li><strong>June 10-15, 2025:</strong> First Merit List</li>
          <li><strong>June 17-20, 2025:</strong> Second Merit List</li>
          <li><strong>June 22-25, 2025:</strong> Third Merit List</li>
          <li><strong>June 27-30, 2025:</strong> Fourth Merit List</li>
          <li><strong>July 1-5, 2025:</strong> Open Category Round</li>
          <li><strong>August 11, 2025:</strong> Start of 11th Classes</li>
        </ul>
      </div>

      <h3>Important Notes</h3>
      <ul>
        <li>Keep all required documents ready before the application period</li>
        <li>Submit your application well before the deadline</li>
        <li>Check your email regularly for updates</li>
        <li>Accept admission within the specified time period</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'Required documents', slug: 'required-documents' },
      { title: 'Merit list process', slug: 'merit-list-process' }
    ]
  },
  'required-documents': {
    title: 'Required Documents',
    category: 'Admission Process',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Documents Required for 11th Admission</h2>
      <p>Make sure you have the following documents ready for the admission process:</p>

      <h3>Essential Documents</h3>
      <ul>
        <li><strong>10th Standard Marksheet</strong> (Original + 2 copies)</li>
        <li><strong>School Leaving Certificate</strong> (Original + 2 copies)</li>
        <li><strong>Birth Certificate</strong> (Original + 2 copies)</li>
        <li><strong>Aadhar Card</strong> (Original + 2 copies)</li>
        <li><strong>Passport Size Photographs</strong> (4 copies)</li>
      </ul>

      <h3>Additional Documents (if applicable)</h3>
      <ul>
        <li><strong>Caste Certificate</strong> (for reserved categories)</li>
        <li><strong>Income Certificate</strong> (for fee concession)</li>
        <li><strong>Disability Certificate</strong> (if applicable)</li>
        <li><strong>Sports Achievement Certificates</strong> (if applicable)</li>
      </ul>

      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
        <p class="text-yellow-800"><strong>Important:</strong> All documents must be self-attested. Keep original documents safe and only submit copies during the admission process.</p>
      </div>
    `,
    relatedArticles: [
      { title: 'Application timeline', slug: 'application-timeline' },
      { title: 'Merit list process', slug: 'merit-list-process' }
    ]
  },
  'merit-list-process': {
    title: 'Merit List Process',
    category: 'Admission Process',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Understanding the Merit List Process</h2>
      <p>The merit list process for 11th admissions follows these steps:</p>

      <h3>Merit List Calculation</h3>
      <ul>
        <li>Based on 10th standard marks (best of 5 subjects)</li>
        <li>Additional weightage for sports achievements</li>
        <li>Reservation categories as per government norms</li>
      </ul>

      <h3>Merit List Rounds</h3>
      <ol>
        <li><strong>First Round:</strong> Based on initial applications</li>
        <li><strong>Second Round:</strong> After first round admissions</li>
        <li><strong>Third Round:</strong> After second round admissions</li>
        <li><strong>Fourth Round:</strong> After third round admissions</li>
        <li><strong>Open Category:</strong> Final round for remaining seats</li>
      </ol>

      <h3>Important Points</h3>
      <ul>
        <li>Accept admission within the specified time period</li>
        <li>Pay fees to confirm admission</li>
        <li>Submit required documents for verification</li>
        <li>Check college notice board for updates</li>
      </ul>
    `,
    relatedArticles: [
      { title: 'Application timeline', slug: 'application-timeline' },
      { title: 'Admission confirmation', slug: 'admission-confirmation' }
    ]
  },
  'admission-confirmation': {
    title: 'Admission Confirmation',
    category: 'Admission Process',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Confirming Your Admission</h2>
      <p>After getting selected in the merit list, follow these steps to confirm your admission:</p>

      <h3>Steps for Confirmation</h3>
      <ol>
        <li>Visit the college within the specified time period</li>
        <li>Submit all required documents</li>
        <li>Pay the admission fees</li>
        <li>Collect the admission receipt</li>
        <li>Complete the college registration process</li>
      </ol>

      <h3>Required Actions</h3>
      <ul>
        <li>Sign the admission form</li>
        <li>Submit passport size photographs</li>
        <li>Complete the student information form</li>
        <li>Attend the orientation program (if scheduled)</li>
      </ul>

      <div class="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
        <p class="text-green-800"><strong>Note:</strong> Keep all receipts and documents safe for future reference.</p>
      </div>
    `,
    relatedArticles: [
      { title: 'Merit list process', slug: 'merit-list-process' },
      { title: 'Required documents', slug: 'required-documents' }
    ]
  },
  'premium-features': {
    title: 'Premium Features',
    category: 'Account & Billing',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Premium Features Overview</h2>
      <p>Upgrade to premium to access these exclusive features:</p>

      <h3>Advanced Features</h3>
      <ul>
        <li><strong>AI-Powered College Predictions:</strong> Get personalized college recommendations</li>
        <li><strong>Cutoff Analysis:</strong> Detailed analysis of college cutoffs</li>
        <li><strong>Priority Support:</strong> Get faster responses to your queries</li>
        <li><strong>Document Verification:</strong> Expert assistance with document submission</li>
      </ul>

      <h3>Premium Benefits</h3>
      <ul>
        <li>Unlimited college comparisons</li>
        <li>Access to historical cutoff data</li>
        <li>Priority in merit list updates</li>
        <li>Exclusive webinars and guidance sessions</li>
      </ul>

      <div class="bg-[#F0E9FF] p-4 rounded-lg mt-6">
        <h3 class="font-semibold mb-2">Premium Plans</h3>
        <ul class="space-y-2">
          <li><strong>Basic:</strong> Essential features for admission process</li>
          <li><strong>Standard:</strong> Advanced features with priority support</li>
          <li><strong>Premium:</strong> All features with expert guidance</li>
        </ul>
      </div>
    `,
    relatedArticles: [
      { title: 'Payment methods', slug: 'payment-methods' },
      { title: 'Cancellation policy', slug: 'cancellation-policy' }
    ]
  },
  'payment-methods': {
    title: 'Payment Methods',
    category: 'Account & Billing',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Available Payment Methods</h2>
      <p>We accept various payment methods for your convenience:</p>

      <h3>Online Payment Options</h3>
      <ul>
        <li>Credit/Debit Cards</li>
        <li>Net Banking</li>
        <li>UPI (Google Pay, PhonePe, etc.)</li>
        <li>Digital Wallets</li>
      </ul>

      <h3>Payment Process</h3>
      <ol>
        <li>Select your preferred plan</li>
        <li>Choose payment method</li>
        <li>Complete the payment</li>
        <li>Receive confirmation email</li>
      </ol>

      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
        <p class="text-yellow-800"><strong>Note:</strong> All payments are secure and encrypted. You will receive a receipt for every transaction.</p>
      </div>
    `,
    relatedArticles: [
      { title: 'Premium features', slug: 'premium-features' },
      { title: 'Cancellation policy', slug: 'cancellation-policy' }
    ]
  },
  'cancellation-policy': {
    title: 'Cancellation Policy',
    category: 'Account & Billing',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Premium Plan Cancellation Policy</h2>
      <p>Our cancellation policy is designed to be fair and transparent:</p>

      <h3>Refund Policy</h3>
      <ul>
        <li><strong>Within 24 hours:</strong> Full refund</li>
        <li><strong>Within 7 days:</strong> 80% refund</li>
        <li><strong>Within 15 days:</strong> 50% refund</li>
        <li><strong>After 15 days:</strong> No refund</li>
      </ul>

      <h3>Cancellation Process</h3>
      <ol>
        <li>Contact customer support</li>
        <li>Provide reason for cancellation</li>
        <li>Complete cancellation form</li>
        <li>Receive refund confirmation</li>
      </ol>

      <div class="bg-[#F0E9FF] p-4 rounded-lg mt-6">
        <p><strong>Note:</strong> Refunds are processed within 7-10 business days to the original payment method.</p>
      </div>
    `,
    relatedArticles: [
      { title: 'Payment methods', slug: 'payment-methods' },
      { title: 'Account security', slug: 'account-security' }
    ]
  },
  'account-security': {
    title: 'Account Security',
    category: 'Account & Billing',
    lastUpdated: 'June 1, 2025',
    content: `
      <h2>Keeping Your Account Secure</h2>
      <p>Follow these guidelines to maintain your account security:</p>

      <h3>Password Guidelines</h3>
      <ul>
        <li>Use a strong, unique password</li>
        <li>Change password regularly</li>
        <li>Don't share your password</li>
        <li>Enable two-factor authentication</li>
      </ul>

      <h3>Security Best Practices</h3>
      <ul>
        <li>Log out after each session</li>
        <li>Don't save passwords on shared devices</li>
        <li>Keep your email secure</li>
        <li>Report suspicious activity</li>
      </ul>

      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
        <p class="text-yellow-800"><strong>Important:</strong> Never share your OTP or verification codes with anyone, including our support team.</p>
      </div>
    `,
    relatedArticles: [
      { title: 'Creating your account', slug: 'creating-account' },
      { title: 'Premium features', slug: 'premium-features' }
    ]
  }
};

const HelpArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? helpArticles[slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Article Not Found</h1>
            <p className="mt-2 text-lg text-gray-600">The article you're looking for doesn't exist.</p>
            <Link to="/help" className="mt-4 inline-block text-indigo-600 hover:text-indigo-500">
              Return to Help Center
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <article className="max-w-3xl mx-auto">
        <header className="mb-8">
          <div className="flex items-center text-sm text-gray-500 mb-2">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{article.category}</span>
            <span className="mx-2">•</span>
            <span>Last updated: {article.lastUpdated}</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{article.title}</h1>
        </header>

        <div 
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {article.relatedArticles && article.relatedArticles.length > 0 && (
          <div className="mt-12 border-t border-gray-200 pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Articles</h2>
            <div className="grid gap-4">
              {article.relatedArticles.map((related, index) => (
                <Link
                  key={index}
                  to={`/help/article/${related.slug}`}
                  className="block p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  <h3 className="text-lg font-medium text-gray-900">{related.title}</h3>
                  <div className="mt-1 flex items-center text-sm text-indigo-600">
                    <span>Read more</span>
                    <svg
                      className="ml-1 w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default HelpArticle;